import { Inject, Injectable } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CurrentStateService } from '@ddv/behaviors';
import { ApiExecutorService, ApiServices, SharedApiExecutorService } from '@ddv/http';
import { forkJoin, map, Observable, ReplaySubject, switchMap } from 'rxjs';

import { FeatureFlagService } from './feature-flag.service';
import { UserData } from '../models/user-data';
import { DDVAPIUserRole, DDVMWEntitlements, Entitlements } from '../models/user-entitlement.model';
import { UserEntitlements } from '../models/user-entitlements';

export interface CrosstalkEntitlements {
    isApprover: boolean;
    canManageConversableType: boolean;
    canAddAndCopyUDFs: boolean;
    canBulkEditUDFs: boolean;
    canImportComments: boolean;
}

@Injectable()
export class UserEntitlementService {
    readonly crosstalkEntitlements$: Observable<CrosstalkEntitlements>;
    private readonly crosstalkEntitlements = new ReplaySubject<CrosstalkEntitlements>(1);

    private clientCode = '';

    constructor(
        private readonly currentStateService: CurrentStateService,
        private readonly featureFlags: FeatureFlagService,
        @Inject(ApiServices.ddvMW) private readonly ddvMWService: ApiExecutorService,
        @Inject(ApiServices.ddvApi) private readonly ddvApiService: ApiExecutorService,
        @Inject(ApiServices.wauth) private readonly wauthApiService: SharedApiExecutorService,
        @Inject(ApiServices.xtlk) private readonly xtlkApiService: ApiExecutorService,
    ) {
        this.crosstalkEntitlements$ = this.crosstalkEntitlements.asObservable();

        this.currentStateService.clientCode$.pipe(
            switchMap((clientCode) => {
                this.clientCode = clientCode;
                return this.fetchCrosstalkUserRoles(this.clientCode);
            }),
        ).subscribe({
            next: (crosstalkEntitlements: CrosstalkEntitlements) => { this.crosstalkEntitlements.next(crosstalkEntitlements); },
        });
    }

    fetchUserData(): Observable<UserData> {
        return this.wauthApiService.invokeServiceWithParams<UserData>('/user');
    }

    fetchUserEntitlements(clientCode: string): Observable<UserEntitlements> {
        return this.featureFlags.isFlagEnabled('use-aws-for-ddv-entitlements', clientCode).pipe(
            switchMap((useAwsApi) => {
                if (useAwsApi) {
                    return this.ddvApiService.invokeServiceWithParams<Entitlements>(clientCode, 'entitlements')
                        .pipe(
                            map((entitlements) => new UserEntitlements(entitlements.entitlements, entitlements.user)),
                        );
                }

                return forkJoin([
                    this.fetchMWEntitlements(clientCode),
                    this.fetchAPIUserRole(clientCode),
                ]).pipe(
                    map((entitlements) => this.buildUserEntitlements(entitlements)),
                );
            }),
        );
    }

    // This should be removed once we start using the API entitlements only
    private buildUserEntitlements(entitlements: [DDVMWEntitlements, DDVAPIUserRole]): UserEntitlements {
        const user = { ...entitlements[0].user, ...entitlements[1] };
        return new UserEntitlements(entitlements[0].entitlements, user);
    }

    private fetchCrosstalkUserRoles(clientCode: string): Observable<CrosstalkEntitlements> {
        return this.xtlkApiService.invokeServiceWithParams<CrosstalkEntitlements>(clientCode, '/user-roles');
    }

    // This should be removed once we start using the API entitlements only
    private fetchMWEntitlements(clientCode: string): Observable<DDVMWEntitlements> {
        return this.ddvMWService.invokeServiceWithParams<DDVMWEntitlements>(clientCode, 'entitlements');
    }

    // This should be removed once we start using the API entitlements only
    private fetchAPIUserRole(clientCode: string): Observable<DDVAPIUserRole> {
        return this.ddvApiService.invokeServiceWithParams<DDVAPIUserRole>(clientCode, 'user-role');
    }
}
